import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../App';
import Layout from '../components/Layout';
import PullToRefresh from '../components/PullToRefresh';
import { authAxios } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Play, ListOrdered, Users, ChevronRight, Trophy } from 'lucide-react';

export default function MyRoster() {
  const { currentSeason, API, user } = useContext(AppContext);
  const [roster, setRoster] = useState([]);
  const [allContestants, setAllContestants] = useState([]);
  const [tribes, setTribes] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [draftStatus, setDraftStatus] = useState(null);
  const [myTotalPoints, setMyTotalPoints] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [leaderboardSize, setLeaderboardSize] = useState(0);
  const [contestantPoints, setContestantPoints] = useState({});
  
  // Draft replay state
  const [showDraftReplay, setShowDraftReplay] = useState(false);
  const [draftReplayData, setDraftReplayData] = useState(null);
  const [replayMode, setReplayMode] = useState('picks'); // 'picks' or 'rankings'
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [replayStep, setReplayStep] = useState(0);
  const [isReplaying, setIsReplaying] = useState(false);

  // Check if draft has completed
  const isDraftCompleted = draftStatus === 'completed';
  const isDraftInProgress = draftStatus === 'in_progress';
  const isDraftNotStarted = !draftStatus || draftStatus === 'not_started';

  useEffect(() => {
    if (currentSeason) {
      loadData();
    }
  }, [currentSeason]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Get season draft status
      const seasonDraftStatus = currentSeason?.draft_status || 'not_started';
      setDraftStatus(seasonDraftStatus);
      
      // Always load tribes
      const tribesRes = await authAxios.get(`${API}/seasons/${currentSeason.id}/tribes`);
      const tribesMap = {};
      tribesRes.data.forEach(tribe => {
        tribesMap[tribe.id] = tribe;
      });
      setTribes(tribesMap);
      
      if (seasonDraftStatus === 'completed') {
        // After draft: Load user's roster + leaderboard + scorecard
        const [rosterRes, leaderboardRes, scorecardRes] = await Promise.all([
          authAxios.get(`${API}/seasons/${currentSeason.id}/my-roster`),
          authAxios.get(`${API}/seasons/${currentSeason.id}/leaderboard`).catch(() => ({ data: [] })),
          authAxios.get(`${API}/seasons/${currentSeason.id}/scorecard`).catch(() => ({ data: [] })),
        ]);
        setRoster(rosterRes.data);
        setAllContestants([]);

        const lb = leaderboardRes.data || [];
        setLeaderboardSize(lb.length);
        const myEntry = lb.find(entry => entry.user_id === user?.id);
        setMyTotalPoints(myEntry ? myEntry.total_points : 0);
        const idx = lb.findIndex(entry => entry.user_id === user?.id);
        setMyRank(idx >= 0 ? idx + 1 : null);

        // Per-contestant point totals for the logged-in user
        const points = {};
        (scorecardRes.data || []).forEach(e => {
          if (e.user_id === user?.id && e.contestant_id && e.contestant_id !== 'BONUS') {
            points[e.contestant_id] = (points[e.contestant_id] || 0) + (e.points || 0);
          }
        });
        setContestantPoints(points);
      } else {
        // Before/during draft: Load all contestants
        const contestantsRes = await authAxios.get(`${API}/seasons/${currentSeason.id}/contestants`);
        setAllContestants(contestantsRes.data);
        setRoster([]);
        setMyTotalPoints(null);
        setMyRank(null);
        setContestantPoints({});
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDraftReplayData = async () => {
    try {
      const res = await authAxios.get(`${API}/seasons/${currentSeason.id}/draft/all-rankings`);
      setDraftReplayData(res.data);
      if (res.data.players?.length > 0) {
        setSelectedPlayerId(res.data.players[0].user_id);
      }
    } catch (error) {
      console.error('Failed to load draft replay data:', error);
    }
  };

  const handleOpenDraftReplay = async () => {
    setShowDraftReplay(true);
    setReplayStep(0);
    setIsReplaying(false);
    if (!draftReplayData) {
      await loadDraftReplayData();
    }
  };

  const startReplay = () => {
    setReplayStep(0);
    setIsReplaying(true);
    
    // Animate through picks
    const picks = draftReplayData?.draft_picks || [];
    let step = 0;
    const interval = setInterval(() => {
      step++;
      setReplayStep(step);
      if (step >= picks.length) {
        clearInterval(interval);
        setIsReplaying(false);
      }
    }, 800);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge badge-active',
      voted_out: 'badge badge-voted-out',
      jury: 'badge badge-jury',
      winner: 'badge badge-winner'
    };
    return badges[status] || 'badge';
  };

  // Stats for the header
  const displayContestants = isDraftCompleted ? roster.map(r => r.contestant) : allContestants;
  const activeCount = displayContestants.filter(c => c?.status === 'active' || c?.status === 'ftc').length;
  const eliminatedCount = displayContestants.filter(c => c?.status === 'voted_out' || c?.status === 'jury' || c?.status === 'removed').length;

  // Render a contestant card
  const renderContestantCard = (contestant, entry = null) => {
    const tribe = tribes[contestant?.tribe_id];
    const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(contestant?.full_name || 'C')}&background=64748b&color=fff&size=80`;
    const pts = isDraftCompleted && contestant?.id != null ? contestantPoints[contestant.id] : undefined;
    const showPts = isDraftCompleted && pts !== undefined;
    
    return (
      <Card key={entry?.id || contestant?.id} className="hover:shadow-lg transition-shadow flex flex-col h-full relative">
        {showPts && (
          <div
            className={`absolute top-2 right-2 z-10 px-2 py-0.5 rounded-full text-xs font-bold shadow-sm ${
              pts > 0
                ? 'bg-amber-500 text-white'
                : pts < 0
                  ? 'bg-red-500 text-white'
                  : 'bg-slate-200 text-slate-700'
            }`}
            data-testid={`contestant-points-${contestant?.id}`}
          >
            {pts > 0 ? `+${pts}` : `${pts}`} pts
          </div>
        )}
        <CardHeader className="p-3 md:pb-3 md:px-6 md:pt-6 flex-shrink-0" style={{ borderTop: `4px solid ${tribe?.color || '#64748b'}` }}>
          <div className="flex flex-col md:flex-row items-center md:items-start md:justify-between mb-2 md:mb-3 gap-2">
            <img 
              src={contestant?.photo_url || fallbackUrl} 
              alt={contestant?.full_name}
              className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-slate-200 cursor-pointer hover:border-amber-400 hover:shadow-lg transition-all"
              onClick={() => setViewingPhoto({ url: contestant?.photo_url || fallbackUrl, name: contestant?.full_name })}
              onError={(e) => {
                if (e.target.src !== fallbackUrl) {
                  e.target.onerror = null;
                  e.target.src = fallbackUrl;
                }
              }}
              referrerPolicy="no-referrer"
            />
            <span className={getStatusBadge(contestant?.status)}>
              {contestant?.status?.replace('_', ' ')}
            </span>
          </div>
          <CardTitle className="text-sm md:text-xl text-slate-800 text-center md:text-left">
            {contestant?.full_name}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 md:p-6 pt-0 flex-grow flex flex-col justify-end">
          <div className="space-y-1.5 md:space-y-2 text-xs md:text-sm">
            {tribe && (
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-1">
                <span className="text-slate-600 text-[10px] md:text-sm">Tribe:</span>
                <span 
                  className="font-medium text-white px-2 py-0.5 md:px-3 md:py-1 rounded-full text-[10px] md:text-xs text-center"
                  style={{ backgroundColor: tribe.color || '#64748b' }}
                >
                  {tribe.name}
                </span>
              </div>
            )}
            {contestant?.age && (
              <div className="flex flex-col md:flex-row md:justify-between gap-1">
                <span className="text-slate-600 text-[10px] md:text-sm">Age:</span>
                <span className="font-medium text-slate-800 text-[10px] md:text-sm">{contestant.age}</span>
              </div>
            )}
            {contestant?.hometown && (
              <div className="flex flex-col md:flex-row md:justify-between gap-1">
                <span className="text-slate-600 text-[10px] md:text-sm">From:</span>
                <span className="font-medium text-slate-800 text-[10px] md:text-sm truncate">{contestant.hometown}</span>
              </div>
            )}
            {contestant?.occupation && (
              <div className="flex flex-col md:flex-row md:justify-between gap-1">
                <span className="text-slate-600 text-[10px] md:text-sm">Job:</span>
                <span className="font-medium text-slate-800 text-[10px] md:text-sm truncate">{contestant.occupation}</span>
              </div>
            )}
            {/* Show acquired_via only after draft */}
            {entry?.acquired_via && (
              <div className="flex flex-col md:flex-row md:justify-between gap-1 pt-1 border-t border-slate-200">
                <span className="text-slate-600 text-[10px] md:text-sm">Acquired via:</span>
                <span className="font-medium text-slate-800 capitalize text-[10px] md:text-sm">{entry.acquired_via}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <PullToRefresh onRefresh={loadData}>
        <div className="max-w-6xl mx-auto container-mobile" data-testid="roster-page">
          <div className="spacing-section">
            <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Roster
            </h1>
            <p className="text-responsive text-slate-600">
              Season {currentSeason?.number} • {activeCount} Active • {eliminatedCount} Eliminated
            </p>
            
            {/* Status banner based on draft state */}
            {isDraftNotStarted && (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800 text-sm font-medium">
                  Meet the Castaways
                </p>
                <p className="text-blue-600 text-xs mt-1">
                  The draft hasn't started yet. Browse all {allContestants.length} contestants below!
                </p>
              </div>
            )}
            
            {isDraftInProgress && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-amber-800 text-sm font-medium">
                  Draft in Progress
                </p>
                <p className="text-amber-600 text-xs mt-1">
                  The draft is currently underway. Your roster will appear here once the draft is complete.
                </p>
              </div>
            )}
            
            {isDraftCompleted && roster.length > 0 && (
              <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-4 sm:gap-6">
                  <div>
                    <p className="text-emerald-800 text-sm font-medium">
                      Your Team
                    </p>
                    <p className="text-emerald-600 text-xs mt-1">
                      You have {roster.length} contestant{roster.length !== 1 ? 's' : ''} on your roster.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 pl-3 sm:pl-4 border-l border-emerald-200" data-testid="my-roster-total-points">
                    <Trophy className="w-5 h-5 text-amber-500 hidden sm:block" />
                    <div>
                      <p className="text-2xl font-bold text-emerald-700 leading-none">
                        {myTotalPoints ?? 0}
                      </p>
                      <p className="text-emerald-600 text-[10px] uppercase tracking-wide mt-1">
                        Total Points{myRank ? ` • Rank #${myRank}${leaderboardSize ? `/${leaderboardSize}` : ''}` : ''}
                      </p>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={handleOpenDraftReplay}
                  variant="outline"
                  className="border-emerald-600 text-emerald-700 hover:bg-emerald-100"
                  data-testid="draft-replay-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  View Draft Recap
                </Button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
            </div>
          ) : isDraftCompleted ? (
            // After draft: Show user's roster
            roster.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <p className="text-slate-600 text-lg">You don't have any contestants yet.</p>
                  <p className="text-slate-500 text-sm mt-2">Check the draft page to see if it's your turn to pick.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                {roster.map((entry) => renderContestantCard(entry.contestant, entry))}
              </div>
            )
          ) : (
            // Before/during draft: Show all contestants
            allContestants.length === 0 ? (
              <Card>
                <CardContent className="text-center py-12">
                  <p className="text-slate-600 text-lg">No contestants added yet.</p>
                  <p className="text-slate-500 text-sm mt-2">The league admin will add contestants before the draft begins.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
                {allContestants.map((contestant) => renderContestantCard(contestant))}
              </div>
            )
          )}

          {/* Photo Viewer Dialog */}
          <Dialog open={!!viewingPhoto} onOpenChange={(open) => !open && setViewingPhoto(null)}>
            <DialogContent className="max-w-2xl p-0 overflow-hidden">
              {viewingPhoto && (
                <div className="relative">
                  <button
                    onClick={() => setViewingPhoto(null)}
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
                    aria-label="Close"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="bg-slate-900 p-8 flex items-center justify-center min-h-[400px]">
                    <img
                      src={viewingPhoto.url}
                      alt={viewingPhoto.name}
                      className="max-w-full max-h-[600px] object-contain rounded-lg"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400/64748b/ffffff?text=' + viewingPhoto.name.charAt(0);
                      }}
                    />
                  </div>
                  <div className="bg-white p-4 border-t">
                    <p className="text-lg font-semibold text-slate-800">{viewingPhoto.name}</p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Draft Replay Dialog */}
          <Dialog open={showDraftReplay} onOpenChange={setShowDraftReplay}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Draft Recap
                </DialogTitle>
              </DialogHeader>
              
              {/* Mode Tabs */}
              <div className="flex gap-2 border-b pb-3">
                <Button
                  variant={replayMode === 'picks' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setReplayMode('picks')}
                  className={replayMode === 'picks' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Replay Draft
                </Button>
                <Button
                  variant={replayMode === 'rankings' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setReplayMode('rankings')}
                  className={replayMode === 'rankings' ? 'bg-amber-600 hover:bg-amber-700' : ''}
                >
                  <ListOrdered className="w-4 h-4 mr-2" />
                  View Rankings
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto">
                {!draftReplayData ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
                  </div>
                ) : replayMode === 'picks' ? (
                  // Draft Replay View
                  <div className="space-y-4 p-2">
                    {!isReplaying && replayStep === 0 && (
                      <div className="text-center py-6">
                        <p className="text-slate-600 mb-4">Watch the draft unfold pick by pick</p>
                        <Button onClick={startReplay} className="bg-amber-600 hover:bg-amber-700">
                          <Play className="w-4 h-4 mr-2" />
                          Start Replay
                        </Button>
                      </div>
                    )}
                    
                    {(isReplaying || replayStep > 0) && (
                      <div className="space-y-2">
                        {draftReplayData.draft_picks?.slice(0, replayStep).map((pick, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-300 ${
                              idx === replayStep - 1 ? 'bg-amber-50 border-amber-300 scale-[1.02]' : 'bg-white border-slate-200'
                            }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold">
                              {pick.pick_number}
                            </div>
                            <div 
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: pick.player_color }}
                            />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{pick.contestant_name}</p>
                              <p className="text-xs text-slate-500">Picked by {pick.player_name}</p>
                            </div>
                            {pick.is_auto_pick && (
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">Auto</span>
                            )}
                          </div>
                        ))}
                        
                        {!isReplaying && replayStep === draftReplayData.draft_picks?.length && (
                          <div className="text-center py-4 border-t mt-4">
                            <p className="text-emerald-600 font-medium">Draft Complete!</p>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => { setReplayStep(0); setIsReplaying(false); }}
                              className="mt-2"
                            >
                              Watch Again
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  // Rankings View
                  <div className="space-y-4 p-2">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-slate-400" />
                      <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Select a player" />
                        </SelectTrigger>
                        <SelectContent>
                          {draftReplayData.players?.map((player) => (
                            <SelectItem key={player.user_id} value={player.user_id}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: player.color }}
                                />
                                {player.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {selectedPlayerId && (
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600 font-medium">
                          {draftReplayData.players?.find(p => p.user_id === selectedPlayerId)?.name}'s Rankings:
                        </p>
                        {draftReplayData.players?.find(p => p.user_id === selectedPlayerId)?.rankings?.map((rank) => (
                          <div 
                            key={rank.rank}
                            className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 bg-white"
                          >
                            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-700">
                              {rank.rank}
                            </div>
                            {rank.contestant_photo && (
                              <img 
                                src={rank.contestant_photo} 
                                alt={rank.contestant_name}
                                className="w-10 h-10 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://via.placeholder.com/40/64748b/ffffff?text=${rank.contestant_name?.charAt(0)}`;
                                }}
                              />
                            )}
                            <span className="font-medium text-slate-800">{rank.contestant_name}</span>
                          </div>
                        ))}
                        
                        {(!draftReplayData.players?.find(p => p.user_id === selectedPlayerId)?.rankings?.length) && (
                          <p className="text-center text-slate-500 py-4">No rankings submitted</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </PullToRefresh>
    </Layout>
  );
}
